// @dynamic-i18n-keys
// Dynamic t() at lines 107, 113, 128, 143: itemLabelKeys.makeCover, moveTo, copyTo, labelAs.
//   mediapopup.itemMenu.makeCover
//   mediapopup.itemMenu.moveTo
//   mediapopup.itemMenu.copyTo
//   mediapopup.itemMenu.labelAs
//   mediapopup.itemMenu.deleteFrom
import React, { useCallback } from 'react';
import { connectMediaStore } from '../../_hocs/withOwnReduxStore';
import { compose } from 'redux';
import { useTranslation } from 'react-i18next';
import { PapiDeviation } from '@wix/da-papi-types';
import { deselectAll } from '../../actions';
import ItemControlButtons from '../ItemControlButtons';
import CameraIcon from '@wix/da-ds/pkg/Icons/24x24/AddCoverImage';
import MoveToFolderIcon from '@wix/da-ds/pkg/Icons/24x24/MoveToFolder';
import CopyIcon from '@wix/da-ds/pkg/Icons/Copy';
import SettingsIcon from '@wix/da-ds/pkg/Icons/24x24/Settings';
import TrashcanIcon from '@wix/da-ds/pkg/Icons/24x24/Trashcan';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import { yieldToMain } from '@wix/da-hooks/pkg/yieldToMain';
import GallectionListMenu from '../GallectionListMenu';
import withCurrentGallection, {
  WithCurrentGallectionProps,
} from '../../_hocs/withCurrentGallection';
import {
  getFolderIdsDisabledForDeviationCopy,
  canMakeDeviationPremium,
  getPremiumFolderIds,
} from '../../helpers';
import withGallections, {
  WithGallectionsProps,
} from '../../_hocs/withGallections';
import { useManageDeviations } from '../../_hocs/withManageDeviations';
import LabelAsMenu from '../LabelAsMenu/LabelAsMenu';
import itemControlButtonStyles from '../ItemControlButtons/ItemControlButtons.scss';

export interface Props {
  className?: string;
  deviation: PapiDeviation;
}

type DispatchProps = {
  deselectAll: () => void;
};

const enhance = compose(
  withCurrentGallection,
  withGallections,
  connectMediaStore<{}, DispatchProps, Props & WithCurrentGallectionProps>(
    null,
    {
      deselectAll,
    }
  )
);

const GallectionDeviationControlButtons: React.FC<
  Props & WithCurrentGallectionProps & WithGallectionsProps & DispatchProps
> = ({
  deviation,
  gallections,
  currentFolderId,
  className,
  currentGallection,
}) => {
  const { t } = useTranslation();

  const itemLabelKeys = {
    makeCover: 'mediapopup.itemMenu.makeCover',
    moveTo: 'mediapopup.itemMenu.moveTo',
    copyTo: 'mediapopup.itemMenu.copyTo',
    labelAs: 'mediapopup.itemMenu.labelAs',
    deleteFrom: 'mediapopup.itemMenu.deleteFrom',
  };

  const {
    isCopyToSupportedGlobally,
    isMoveToSupportedGlobally,
    isCopyToSupportedInThisFolder,
    isMoveToSupportedInThisFolder,
    isSettingCoverImageSupportedInThisFolder,
    isRemoveSupportedInThisFolder,
    isLabelAsSupportedInThisFolder,
    copyTo,
    moveTo,
    setDeviationAsCover,
    remove,
  } = useManageDeviations([deviation]);

  const showCopyTo = isCopyToSupportedGlobally;
  const showMoveTo = isMoveToSupportedGlobally;
  const showLabelAs = isLabelAsSupportedInThisFolder;
  const isCopyToDisabled = !isCopyToSupportedInThisFolder;
  const isMoveToDisabled = !isMoveToSupportedInThisFolder;
  const isRemoveDisabled = !isRemoveSupportedInThisFolder;

  const removeClickHandler = useCallback(() => {
    yieldToMain(() => {
      remove();
    });
  }, [remove]);

  return (
    <>
      <ItemControlButtons
        className={className}
        moreMenuClassName={itemControlButtonStyles['item-control-menu']}
        menuItems={[
          {
            // safe zone
            items: [
              currentGallection?.type !== 'stash' && {
                label: t(itemLabelKeys.makeCover),
                icon: <CameraIcon size={IconSize.SMALL} />,
                onClick: () => setDeviationAsCover(deviation),
                disabled: !isSettingCoverImageSupportedInThisFolder,
              },
              showMoveTo && {
                label: t(itemLabelKeys.moveTo),
                submenu: (
                  <GallectionListMenu
                    onSelect={moveTo}
                    disableFolderIds={
                      !canMakeDeviationPremium(deviation)
                        ? getPremiumFolderIds(gallections)
                        : []
                    }
                  />
                ),
                disabled: isMoveToDisabled,
                icon: <MoveToFolderIcon size={IconSize.SMALL} />,
              },
              showCopyTo && {
                label: t(itemLabelKeys.copyTo),
                submenu: (
                  <GallectionListMenu
                    onSelect={copyTo}
                    disableFolderIds={getFolderIdsDisabledForDeviationCopy(
                      gallections,
                      currentFolderId,
                      deviation
                    )}
                  />
                ),
                disabled: isCopyToDisabled,
                icon: <CopyIcon size={IconSize.SMALL} />,
              },
              showLabelAs && {
                label: t(itemLabelKeys.labelAs),
                icon: <SettingsIcon size={IconSize.SMALL} />,
                submenu: (
                  <LabelAsMenu
                    gallection={currentGallection}
                    deviationIds={[deviation.deviationId]}
                  />
                ),
              },
            ],
          },
          {
            // danger zone
            items: [
              {
                value: 3,
                label: t!(itemLabelKeys.deleteFrom),
                icon: (
                  <TrashcanIcon size={IconSize.SMALL} disableDefaultColors />
                ),
                danger: true,
                disabled: isRemoveDisabled,
                onClick: removeClickHandler,
              },
            ],
          },
        ]}
      />
    </>
  );
};

export default enhance(
  GallectionDeviationControlButtons
) as React.ComponentType<Props>;
